import React, { useState } from 'react';
import { observer } from 'mobx-react';
import {
  TopBarWrapper, TopBarLeft, TopBarRight,
  CompanySelector, UserInfo, UserAvatar, UserName, HamburgerButton,
  AddButton, ModalOverlay, ModalBox, ModalTitle, ModalInput, ModalActions, ModalButton,
} from './TopBar.styles';

const TopBar = observer(({ currentUser, adminStore, onCompanyChange, onAddBusinessLine, selectedCompanyId, onMenuToggle }) => {
  const [showModal, setShowModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const companies = adminStore?.companies || [];
  const firstName = currentUser?.firstName || '';
  const lastName = currentUser?.lastName || '';
  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  const canAdd = currentUser?.isAdmin || currentUser?.isCompanyAdmin;

  const sortedCompanies = [...companies].sort((a, b) =>
    (a.company.name || '').localeCompare(b.company.name || '')
  );

  const handleSave = async () => {
    if (!newName.trim() || !onAddBusinessLine) return;
    setSaving(true);
    setSaveError('');
    try {
      await onAddBusinessLine(newName.trim());
      setShowModal(false);
      setNewName('');
    } catch (e) {
      // Surface the failure in the modal — a console.error here left the
      // modal hanging with no feedback (how the Fix 3 403 went unnoticed).
      console.error('Failed to add business line:', e);
      setSaveError(e?.message || 'Failed to add business line. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <TopBarWrapper>
        <TopBarLeft>
          <HamburgerButton onClick={onMenuToggle} aria-label="Toggle menu">
            {'\u2630'}
          </HamburgerButton>
          {sortedCompanies.length > 1 && (
            <CompanySelector
              value={selectedCompanyId || ''}
              onChange={(e) => onCompanyChange({ value: e.target.value })}
            >
              {sortedCompanies.map((c) => (
                <option key={c.company.id} value={c.company.id}>
                  {c.company.name}
                </option>
              ))}
            </CompanySelector>
          )}
          {canAdd && (
            <AddButton onClick={() => { setSaveError(''); setShowModal(true); }} title="Add Business Line">+</AddButton>
          )}
        </TopBarLeft>

        <TopBarRight>
          <UserInfo>
            <UserName>{firstName} {lastName}</UserName>
            <UserAvatar>{initials}</UserAvatar>
          </UserInfo>
        </TopBarRight>
      </TopBarWrapper>

      {showModal && (
        <ModalOverlay onClick={() => { if (!saving) { setShowModal(false); setNewName(''); } }}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Add Business Line</ModalTitle>
            <p style={{ margin: '0 0 16px', color: '#64748b', fontSize: 14 }}>
              What is the name of your new business line?
            </p>
            <ModalInput
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Business line name"
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
              disabled={saving}
            />
            {saveError && (
              <p style={{ margin: '8px 0 0', color: '#db2828', fontSize: 13 }}>{saveError}</p>
            )}
            <ModalActions>
              <ModalButton onClick={() => { setShowModal(false); setNewName(''); }} disabled={saving}>
                Cancel
              </ModalButton>
              <ModalButton $primary onClick={handleSave} disabled={saving || !newName.trim()}>
                {saving ? 'Saving...' : 'Save'}
              </ModalButton>
            </ModalActions>
          </ModalBox>
        </ModalOverlay>
      )}
    </>
  );
});

export default TopBar;
